import Sentry from '@integrations/Sentry';
import { FormControl, InputLabel, MenuItem, Select } from '@mui/material';
import FormHelperText from '@mui/material/FormHelperText';
import Link from '@mui/material/Link';
import RoomSchema, { initialValues as _initialValues } from '@schema/RoomSchema';
import SubmitButton from '@ui/components/buttons/SubmitButton';
import { makeErrors, TooltipErrors } from '@ui/components/form-errors/FormErrors';
import { isError } from '@ui/utils/typescriptHelpers';
import debouncePromise from '@utils/debouncePromise';
import { checkSlug } from '@utils/scannerNextChar';
import { Field, Formik } from 'formik';
import { TextField } from 'formik-mui';
import React from 'react';
import slugify from 'slugify';

import { ROOM_TYPES } from '../../data/src/roomTypes';

const debounceCheckSlug = debouncePromise(checkSlug, 300);

const RoomForm = ({ onSubmit, initialValues = _initialValues, error, submitText = 'Continue', homeSlug, homeID }) => {
  return (
    <Formik
      validationSchema={RoomSchema}
      initialValues={initialValues}
      enableReinitialize={false}
      onSubmit={onSubmit}
    >
      {({
        errors,
        touched,
        values,
        handleSubmit,
        isSubmitting,
        setFieldValue,
        setFieldTouched,

      }) => {
        return (
          <form onSubmit={handleSubmit}>
            <Field
              fullWidth
              data-test="title"
              name="title"
              label="Title"
              type="text"
              component={TextField}
              margin="normal"
              variant="outlined"
              onChange={async (e) => {
                setFieldValue('title', e.target.value);
                setFieldTouched('title', true);

                const slug = slugify(e.target.value, {
                  strict: true,
                  lower: true,
                });

                let existingSlugs = [];

                try {
                  existingSlugs = await debounceCheckSlug(slug, `homes/${homeID}/rooms`);
                } catch (e) {
                  Sentry.captureException(e);
                  return;
                }

                if (existingSlugs.length) {
                  let n = 1;
                  let incrememtedSlug = slug;

                  while (existingSlugs.indexOf(incrememtedSlug) > -1) {
                    incrememtedSlug = `${slug}-${n}`;
                    n += 1;
                  }

                  setFieldValue('slug', incrememtedSlug);
                } else {
                  setFieldValue('slug', slug);
                }
              }}
            />

            <Field
              fullWidth
              disabled
              data-test="slug"
              name="slug"
              label="URL"
              type="text"
              component={TextField}
              margin="normal"
              variant="outlined"
              InputProps={{
                startAdornment: (
                  <span>
                    {`https://www.guesthouseshop.com/shop/homes/${homeSlug}/rooms/`}
                    &lrm;
                  </span>
                ),
              }}
            />

            <Field
              fullWidth
              multiline
              data-test="description"
              name="description"
              label="Description"
              type="text"
              component={TextField}
              margin="normal"
              variant="outlined"
              rows={3}
            />

            <Field
              fullWidth
              data-test="sqft"
              name="sqft"
              label="Square feet"
              type="number"
              component={TextField}
              margin="normal"
              variant="outlined"
            />
            
            <FormControl
              fullWidth
              variant="outlined"
              margin="normal"
              error={touched.type && Boolean(errors.type)}
            >
              <InputLabel id="type-label">
                Type
              </InputLabel>
              <Select
                data-test="type"
                labelId="type-label"
                id="type"
                value={values.type || []}
                label="Type"
                onChange={(e) => {
                  setFieldTouched('type', true);
                  setFieldValue('type', e.target.value);
                }}
              >
                {ROOM_TYPES.map(type => (
                  <MenuItem
                    key={type.id}
                    value={type.id}
                  >
                    {type.title}
                  </MenuItem>
                ))}
              </Select>
              {touched.type && Boolean(errors.type) && (
                <FormHelperText error>
                  {errors.type}
                </FormHelperText>
              )}
            </FormControl>

            {error && (
              <>
                <FormHelperText error>
                  {isError(error) ? error.message : error}                
                </FormHelperText>
                <FormHelperText>
                  Need help? Contact 
                  {' '}
                  <Link href="mailto:support@guesthouseshop.com">
                    support@guesthouseshop.com
                  </Link>
                </FormHelperText>
              </>
            )}

            <div style={{ paddingTop: 20 }}>
              <SubmitButton
                fullWidth
                data-test="room-submit-button"
                isSubmitting={isSubmitting}
                disabled={Boolean(Object.keys(errors).length)}
                aria-label={submitText}
                tooltip={makeErrors(errors, touched)?.length && (
                  <TooltipErrors
                    errors={errors}
                    touched={touched}
                  />
                )}
              >
                {submitText}
              </SubmitButton>
            </div>
          </form>
        );
      }}
    </Formik>
  );
};

export default React.memo(RoomForm);
